import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import EntityLink from '../components/EntityLink';

const TABS = ['Profile', 'Work Orders', 'AR Invoices', 'Ledger', 'Documents'];

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ClientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('Profile');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  function load() {
    setError(false);
    api.get(`/clients/${id}`)
      .then(r => { setData(r.data); setForm(r.data); })
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  function sf(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      await api.put(`/clients/${id}`, form);
      load();
      setEditing(false);
    } catch (e) {
      setSaveError(e.response?.data?.error || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  }

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="page-title">{data.name}</h1>
          <Badge variant={data.is_active ? 'success' : 'neutral'}>{data.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} className={'panel-tab' + (tab === t ? ' active' : '')} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Profile' && (
        <ProfileTab
          data={data} editing={editing} form={form} sf={sf}
          onEdit={() => { setEditing(true); setForm(data); setSaveError(''); }}
          onCancel={() => { setEditing(false); setSaveError(''); }}
          onSave={handleSave} saving={saving} saveError={saveError}
        />
      )}

      {tab === 'Work Orders' && <WorkOrdersTab workOrders={data.work_orders} />}

      {tab === 'AR Invoices' && <ARInvoicesTab data={data} />}

      {tab === 'Ledger' && <ClientLedgerTab clientId={data.id} />}

      {tab === 'Documents' && (
        <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
          <DocumentWallet entityType="client" entityId={data.id} entityName={data.name} />
        
      <AuditLog tableName="clients" recordId={id} /></div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function ProfileTab({ data, editing, form, sf, onEdit, onCancel, onSave, saving, saveError }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Client Profile</h3>
        <button className="btn btn-secondary" onClick={editing ? onCancel : onEdit}>{editing ? 'Cancel' : 'Edit'}</button>
      </div>

      {saveError && (
        <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
          {saveError}
        </div>
      )}

      {editing ? (
        <>
          <div className="form-grid">
            <div className="form-field"><label>Client Name</label><input value={form.name || ''} onChange={e => sf('name', e.target.value)} /></div>
            <div className="form-field"><label>Contact Name</label><input value={form.contact_name || ''} onChange={e => sf('contact_name', e.target.value)} /></div>
            <div className="form-field"><label>Email</label><input type="email" value={form.email || ''} onChange={e => sf('email', e.target.value)} /></div>
            <div className="form-field"><label>Phone</label><input value={form.phone || ''} onChange={e => sf('phone', e.target.value)} /></div>
            <div className="form-field"><label>Tax ID</label><input value={form.tax_id || ''} onChange={e => sf('tax_id', e.target.value)} /></div>
            <div className="form-field"><label>Net Terms (days)</label><input type="number" value={form.net_terms || ''} onChange={e => sf('net_terms', e.target.value)} /></div>
            <div className="form-field">
              <label>Status</label>
              <select value={form.is_active ? '1' : '0'} onChange={e => sf('is_active', e.target.value === '1' ? 1 : 0)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Address</label><textarea rows={2} value={form.address || ''} onChange={e => sf('address', e.target.value)} /></div>
          <div className="form-field span-2" style={{ marginTop: 14 }}><label>Notes</label><textarea rows={3} value={form.notes || ''} onChange={e => sf('notes', e.target.value)} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Field label="Contact Name" value={data.contact_name} />
          <Field label="Email" value={data.email} />
          <Field label="Phone" value={data.phone} />
          <Field label="Tax ID" value={data.tax_id} />
          <Field label="Net Terms" value={data.net_terms ? `Net ${data.net_terms}` : null} />
          <div style={{ gridColumn: '1 / -1' }}><Field label="Address" value={data.address} /></div>
          {data.notes && <div style={{ gridColumn: '1 / -1' }}><Field label="Notes" value={data.notes} /></div>}
        </div>
      )}
    </div>
  );
}

function WorkOrdersTab({ workOrders }) {
  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <h3 style={{ marginTop: 0 }}>Work Orders ({(workOrders || []).length})</h3>
      {(!workOrders || workOrders.length === 0) ? (
        <StateBlock type="empty" title="No work orders yet" />
      ) : workOrders.map(wo => (
        <div key={wo.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600 }}>{wo.client_wo_number}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {(wo.engagement_type || '').replace('_', ' ')} · {wo.first_name} {wo.last_name} · <Badge variant={wo.status === 'active' ? 'success' : 'neutral'}>{wo.status}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

function ARInvoicesTab({ data }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Total Billed</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_billed)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Received</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.total_verified_received)}</div>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.outstanding)}</div>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>AR Invoices ({data.ar_invoices.length})</h3>
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Contractor</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'right' }}>Verified Paid</th><th>Status</th></tr></thead>
          <tbody>
            {data.ar_invoices.length === 0 && <tr><td colSpan={6}><StateBlock type="empty" title="No AR invoices" /></td></tr>}
            {data.ar_invoices.map(inv => (
              <tr key={inv.id}>
                <td>{inv.invoice_number}</td>
                <td>{inv.first_name} {inv.last_name}</td>
                <td>{inv.invoice_date}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.amount)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(inv.verified_paid)}</td>
                <td><Badge>{inv.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ClientLedgerTab({ clientId }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    setLedger(null);
    api.get(`/clients/${clientId}/ledger`).then(r => setLedger(r.data));
  }, [clientId]);

  if (!ledger) return <StateBlock type="loading" />;
  if (ledger.rows.length === 0) return <StateBlock type="empty" title="No ledger activity yet" />;

  return (
    <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Ledger Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(ledger.summary.ledger_balance)}</div>
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Verified Balance</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{fmt(ledger.summary.verified_balance)}</div>
        </div>
      </div>
      <table className="data-table">
        <thead><tr><th>Invoice #</th><th style={{ textAlign: 'right' }}>Amount</th><th>Paid</th><th>Status</th></tr></thead>
        <tbody>
          {ledger.rows.map((r, i) => (
            <tr key={i}>
<td><EntityLink type="ar_invoice" id={r.invoice_id}>{r.invoice_number}</EntityLink></td>
              <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
              <td>{r.payment_date || '—'}</td>
              <td>{r.verification_status ? <Badge variant={r.verification_status === 'verified' ? 'success' : 'warning'}>{r.verification_status}</Badge> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
